import { Box } from "@mui/material";
import { FormikProps, getIn } from 'formik';
import TooltipText from "../inputComponents/TooltipText";
import { InputComponent } from "../inputComponents/InputComponent";
import { PluginStoreTempConfig } from "../../types/configuration";
import { ICreatePromotionState } from "../../types/componentTypes/assignPromo";
import { ToolTipPlacement } from "../../types/componentTypes/tooltip";
import { NavBar } from '../../common/NavBar/NavBar'
import '../../pages/EditPromotion/ProofOfPurchase/ProofOfPurchase.css';
import { useState, SyntheticEvent, useEffect } from 'react';
import { languageCodes } from '../../constants/lists';
import { AddLanguageTabs } from '../../components/modals/AddLanguageTabs';

type PluginStoreDetailsProps =
{
    isEditPage: boolean;
    formik: FormikProps<PluginStoreTempConfig>;
}
|
{
    isEditPage: boolean;
    formik: FormikProps<ICreatePromotionState>;
}

const getInputValues = (getFormValuePath) => [
    {
        dataTestId: 'pluginParams.emailTemplateId',
        valuePath: 'pluginParams.emailTemplateId',
        label: 'AJO email id for successful processing',
    },
    {
        dataTestId: 'pluginParams.rejectedEmailTemplateId',
        valuePath: 'pluginParams.rejectedEmailTemplateId',
        label: 'AJO email id for rejected processing',
    },
    {
        dataTestId: 'scannerConfigInstanceId',
        valuePath: getFormValuePath('scannerConfigInstanceId'),
        label: 'RS config instance id',
    },
];
function PluginStoreDetails({ formik, isEditPage = false }: Partial<PluginStoreDetailsProps>) {
    const [tabLabels, setTabLabels] = useState<Array<string>>([]);
    const [open, setOpen] = useState(false);
    const [customIndex, setCustomIndex] = useState<number | null>(1);
    const [language] = useState("");

    const handleModalOpen = (event: SyntheticEvent, index: number) => {
        if (index === 0) {
            setOpen(true)
        }
    }


    const getFormValuePath = (val: 'timeoutInMillis' | 'scannerConfigInstanceId'): string => {
        const basePath = 'pluginRoutes.processReceipts.externalReqParams';
        const promoFlow = getIn(formik.values, 'tempParams.promoFlow') || '';
        return (promoFlow === '' ? `${basePath}.${val}` : `flow.${promoFlow}.${basePath}.${val}`);
    };

    const rsFormikPathForField = (field: 'uploadImage' | 'pluginRoutes') => {
        if (!isEditPage) return field;

        const promoFlow = getIn(formik.values, 'tempParams.promoFlow');
        return field === 'uploadImage'
            ? `flow.${field}`
            : `flow.${promoFlow}.${field}`;
    }

    const setRSProcessingMarket = () => {
        const marketInProcessingFlow = `${rsFormikPathForField('pluginRoutes')}.processReceipts.externalReqParams.market`;
        const currentMarket = getIn(formik.values, marketInProcessingFlow);
        const shouldInitializeMarket = currentMarket == null || `${currentMarket}`.trim() === '';

        if (shouldInitializeMarket) {
            const configuredMarket = formik.values?.configurationParameters?.country;
            const normalizedMarket = configuredMarket == null ? '' : String(configuredMarket).trim();
            formik.setFieldValue(marketInProcessingFlow, normalizedMarket);
        }
    }


    const inputFormValues = getInputValues(getFormValuePath);
    const handleTabDeleteFromFormikState = (language?: string): void => {
        if (!language) return;

        setTabLabels((prevTabs) => prevTabs.filter((tab) => tab !== language));
        const languageCode = Object.keys(languageCodes).find(key => languageCodes[key] === language);

        if (languageCode) {
            const updatedUrl = { ...formik.values.pluginParams.url };
            delete updatedUrl[languageCode];
            formik.setFieldValue('pluginParams.url', updatedUrl);

            if(formik.initialValues.pluginParams.url[languageCode]){
                delete formik.initialValues.pluginParams.url[languageCode];
            }
        }
    };

    const replaceKeyInObject = (obj, oldKey, newKey) => {
        return Object.keys(obj).reduce((acc, key) => {
            if (key === oldKey){
                acc[newKey] = obj[oldKey];
            } else {
                acc[key] = obj[key];
            }
            return acc;
        }, {})
    }


    useEffect(() => {
        setRSProcessingMarket();
        let languageFromFormik = getIn(formik.values, 'configurationParameters.language');
        if (!languageCodes[languageFromFormik] ) {
            languageFromFormik = Object.keys(languageCodes).find(lang => languageCodes[lang] === languageFromFormik);
        }
        if(!languageFromFormik) return;

        const urlList = getIn(formik.values, 'pluginParams.url') || {};
        const objectKeys = Object.keys(urlList);
        let shouldUpdateTabs = false;
        const currentKeys = objectKeys[0];

        if(objectKeys.length === 0){
            formik.setFieldValue('pluginParams.url', { [languageFromFormik]: ''});
            shouldUpdateTabs = true;
        } else if(currentKeys !== languageFromFormik) {
            const newUrlList = replaceKeyInObject(urlList,currentKeys,languageFromFormik);
            formik.setFieldValue('pluginParams.url', newUrlList);
            shouldUpdateTabs = true;
        }

        const initialLang = Object.keys(formik.values.pluginParams?.url || {}).map(
            lang => languageCodes[lang] || lang
        )


        const uniqueTabs = new Set([
            '+ ADD NEW LANGUAGE',
            ...initialLang,
        ]);

        const tabsArray = Array.from(uniqueTabs);
        setTabLabels(tabsArray);

        if(shouldUpdateTabs) {
            setTabLabels(['+ ADD NEW LANGUAGE', languageCodes[languageFromFormik]]);
        } else {
            setTabLabels(tabsArray);
        }

        if (tabsArray.length > 1 && !formik.dirty) {
            const languageDisplay = languageCodes[languageFromFormik]
            const languageTabIndex = tabsArray.findIndex(tab => tab === languageDisplay);
            if (languageTabIndex > 0) {
                setCustomIndex(languageTabIndex);
            }
        }

    // eslint-disable-next-line
    }, [isEditPage, language, formik]);

    const urlValues = getIn(formik.values, 'pluginParams.url') || {};
    tabLabels.forEach(tab => {
        if (tab !== '+ ADD NEW LANGUAGE') {
            const code = Object.entries(languageCodes)
                .find(([, display]) => display === tab)?.[0];
            if (code && !(code in urlValues)) {
                formik.setFieldValue(`pluginParams.url.${code}`, '');
            }
        }
    });
    const tabs = Array.from(new Set(tabLabels));
    const tabComponents = [];

    tabs.forEach((value: string, index: number) => {
        if (value?.toLocaleLowerCase().includes('add')) {
            tabComponents.splice(0, 0, (
                <AddLanguageTabs
                    modalOpenState={open}
                    tabs={tabs}
                    country={formik.values?.configurationParameters?.country || ''}
                    handleClose={setOpen}
                    handleCustomIndex={setCustomIndex}
                    setTabLabels={setTabLabels}
                    languageFieldName="pluginParams.url"
                />
            ));
        } else {

        const languageCode = Object.keys(languageCodes).find(key => languageCodes[key] === value);

         if (languageCode) {

            tabComponents.splice(index, 0, (
                    <InputComponent
                        key={`pluginParams.url.${languageCode}`}
                        inputClassName="plugin-store-input"
                        formik={formik}
                        dataTestId={`pluginParams.url`}
                        inputValue={getIn(formik.values, `pluginParams.url.${languageCode}`)}
                        formikLabel={`pluginParams.url.${languageCode}`}
                        label={`Return URL for Successful RS`}
                        errorPath={`pluginParams.url`}
                    />

            ));
        }
    }
    });

    return (
        <Box sx={{ width: 430, flexGrow: 0, flexShrink: 0, alignSelf: 'flex-start' }} >
                <Box >
                    {inputFormValues.map(({ dataTestId, valuePath, label }) => (
                        <InputComponent
                            key={dataTestId}
                            inputClassName="plugin-store-input"
                            formik={formik}
                            dataTestId={dataTestId}
                            inputValue={getIn(formik.values, valuePath)}
                            formikLabel={valuePath}
                            label={label}
                        />
                ))}
                    <Box sx={{position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '16px' }}>
                        <TooltipText
                            textVariant="h4"
                            styleName='plugin-store-tooltip'
                            text=''
                            tooltip='Choose RS processing timeout in milliseconds. This timeout is how long will the Receipt Scanner process your receipt.
                            Default value is 8000ms (8s)'
                            placement={ToolTipPlacement.Right}
                        />
                        <Box sx={{ width: '100%', mt: 4 }}>
    <InputComponent
      inputClassName="plugin-store-input-timeout"
      formik={formik}
      inputValue={getIn(formik.values, getFormValuePath('timeoutInMillis'))}
      formikLabel={getFormValuePath('timeoutInMillis')}
      label='Rs processing timeout'
    />
  </Box>
                    </Box>
                    <NavBar enableDirectionSelectArrows={true} deleteTabButtonCustomAction={handleTabDeleteFromFormikState}  enableTabRemove={true} tabLabels={tabs} customAction={handleModalOpen} components={tabComponents} navClass='nav-tabs' customTabIndex={customIndex} resetCustomIndex={setCustomIndex} rsClass={isEditPage ? '' : 'rsTabs'}/>
                </Box>
        </Box>
    )
}

export { PluginStoreDetails }
